import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormGroup } from '@angular/forms';
import { InputItemModel, InputListModel } from '@libs/form/models';

@Injectable()
export class InputListService {
  /**
   * Removes a row at the specified index by shifting values up and removing the last control
   * This is the preferred method as it's more efficient than shifting controls
   *
   * @param control The parent FormGroup containing the row controls
   * @param rowsLength The current number of rows
   * @param index The index of the row to remove
   */
  public removeRowControl(control: UntypedFormGroup, rowsLength: number, index: number): void {
    // Shift subsequent control values to new positions
    this.shiftControlValuesUp(control, rowsLength, index);

    // Remove the last control
    control.removeControl((rowsLength - 1).toString());
  }

  /**
   * Shifts control values up starting from the specified index
   * Used when removing a row - values are shifted instead of controls
   *
   * @param control The parent FormGroup containing the row controls
   * @param rowsLength The current number of rows
   * @param index The index from which to start shifting values up
   */
  public shiftControlValuesUp(control: UntypedFormGroup, rowsLength: number, index: number): void {
    for (let i: number = index; i < rowsLength - 1; i++) {
      const currentIndexControl: AbstractControl = control.controls[i.toString()];
      const nextIndexControl: AbstractControl = control.controls[(i + 1).toString()];

      currentIndexControl.setValue(nextIndexControl.value);
    }
  }

  /**
   * Builds a template for new rows without description and value
   *
   * @param data The original InputListModel
   */
  public buildInputListTemplate(data: InputListModel): InputListModel {
    const items: InputItemModel[] = data.items.map(item => {
      return {
        ...item,
        description: undefined,
        value: undefined,
      };
    });

    return {
      ...data,
      items,
    };
  }

  /**
   * Adds subscriptSizing: 'dynamic' to all items
   *
   * @param data The original InputListModel
   */
  public normalizeInputListData(data: InputListModel): InputListModel {
    return {
      ...data,
      items: data.items.map((item) => {
        return {
          ...item,
        };
      }),
    };
  }

  /**
   * Adds a new control at the specified index
   *
   * @param control The parent FormGroup
   * @param index The index where the new control should be added
   * @param data The input list item configuration
   */
  public addRowControl(control: UntypedFormGroup, index: number, data: InputListModel): void {
    const group: FormGroup = new FormGroup({});

    data.items.forEach(item => {
      group.addControl(item.name, new FormControl());
    });

    control.setControl(index.toString(), group);
  }

  /**
   * Shifts controls to make space for a new row at the specified index
   *
   * @param control The parent FormGroup
   * @param rowsLength Current number of rows
   * @param insertIndex The index where the new row will be inserted
   */
  public shiftControlsForInsert(control: UntypedFormGroup, rowsLength: number, insertIndex: number): void {
    // Start from the end and move backwards to avoid overwriting
    for (let i: number = rowsLength - 1; i > insertIndex; i--) {
      const controlToMove: AbstractControl | null = control.get((i - 1).toString());

      if (controlToMove) {
        control.setControl(i.toString(), controlToMove);
      }
    }
  }
}
